// screens/ShareScreen.js
// BuzzInApp — Pantalla de compartir link único
//
// Mejoras v2:
//   • Hero container con glow + clase --ready cuando canClaim
//   • Número de clicks con pulso suave cuando está listo
//   • Barra de progreso con clases dinámicas (--active, --done)
//   • Botón de copia con estado verde confirmado
//   • Steps con borde acento en los números

import React, { useState } from "react";
import useClicks from "../hooks/useClicks";
import useTranslation from "../i18n/useTranslation";
import "./ShareScreen.css";

// ─── Iconos ───────────────────────────────────────────────────────────────────
const IconWhatsApp = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const IconEmail = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M2 8L12 13L22 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const IconCopy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="1.8"/>
  </svg>
);

const IconShare = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconBack = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Componente ───────────────────────────────────────────────────────────────
const ShareScreen = ({ business, authHook, onBack }) => {
  const { t } = useTranslation();
  const { user } = authHook;

  const {
    shareUrl,
    clickCount,
    clicksToGo,
    canClaim,
    progressPct,
    loadingLink,
    copyLink,
    shareWhatsApp,
    shareEmail,
    shareNative,
  } = useClicks(user?.uid, business);

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyLink();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  if (!business) return null;

  // Clases dinámicas para el hero y la barra
  const heroClass = [
    "share-screen__clicks-hero",
    canClaim ? "share-screen__clicks-hero--ready" : "",
  ].join(" ").trim();

  const fillClass = [
    "share-screen__progress-fill",
    progressPct >= 100 ? "share-screen__progress-fill--done"
      : progressPct > 0 ? "share-screen__progress-fill--active"
      : "",
  ].join(" ").trim();

  return (
    <div className="share-screen">
      {/* Header */}
      <header className="share-screen__header">
        <button
          className="share-screen__back"
          onClick={onBack}
          aria-label={t("app.back")}
        >
          <IconBack />
        </button>
        <div className="share-screen__header-title">
          <span className="share-screen__biz-icon">{business.icon || "🏪"}</span>
          <span>{business.name}</span>
        </div>
        <div style={{ width: 40 }} />
      </header>

      <div className="share-screen__body">
        {/* Contador hero */}
        <div className={heroClass} aria-live="polite" aria-atomic="true">
          <span className="share-screen__clicks-number">{clickCount}</span>
          <span className="share-screen__clicks-label">
            {clickCount === 0
              ? t("share.clickCountZero")
              : clickCount === 1
              ? t("share.clickCountOne")
              : t("share.clickCount", { n: clickCount })
            }
          </span>

          {/* Barra de progreso */}
          <div className="share-screen__progress-wrap">
            <div
              className="share-screen__progress"
              aria-label={`${progressPct}% completado`}
            >
              <div className={fillClass} style={{ width: `${progressPct}%` }} />
            </div>
            <span className={`share-screen__progress-label ${canClaim ? "share-screen__progress-label--ready" : ""}`}>
              {canClaim
                ? "✓ " + t("bizCard.claimButton")
                : t("home.clicksNeeded", { n: clicksToGo })
              }
            </span>
          </div>
        </div>

        {/* Premio */}
        <div className="share-screen__prize-box">
          <div className="share-screen__prize-you">
            {t("share.prize", { prize: business.prize_user })}
          </div>
          {business.prize_friends && (
            <div className="share-screen__prize-friends">
              {t("share.friendPrize", { prize: business.prize_friends })}
            </div>
          )}
        </div>

        {/* Link */}
        {loadingLink ? (
          <div className="share-screen__link-loading" aria-live="polite">
            <span className="share-screen__spinner" />
          </div>
        ) : (
          <div className="share-screen__link-box">
            <span className="share-screen__link-text" aria-label="Tu link único">
              {shareUrl}
            </span>
            <button
              className={`share-screen__copy-btn ${copied ? "share-screen__copy-btn--copied" : ""}`}
              onClick={handleCopy}
              aria-label={copied ? t("share.linkCopied") : t("share.copyLink")}
            >
              {copied ? <IconCheck /> : <IconCopy />}
            </button>
          </div>
        )}

        {/* Botones de compartir */}
        <div className="share-screen__actions">
          <button
            className="share-screen__btn share-screen__btn--whatsapp"
            onClick={shareWhatsApp}
            aria-label={t("share.shareWhatsApp")}
          >
            <IconWhatsApp />
            {t("share.shareWhatsApp")}
          </button>

          <button
            className="share-screen__btn share-screen__btn--email"
            onClick={shareEmail}
            aria-label={t("share.shareEmail")}
          >
            <IconEmail />
            {t("share.shareEmail")}
          </button>

          {"share" in navigator && (
            <button
              className="share-screen__btn share-screen__btn--other"
              onClick={shareNative}
              aria-label={t("share.shareOther")}
            >
              <IconShare />
              {t("share.shareOther")}
            </button>
          )}
        </div>

        {/* Cómo funciona */}
        <div className="share-screen__how">
          <h3 className="share-screen__how-title">{t("share.howItWorks")}</h3>
          <ol className="share-screen__steps">
            {["step1","step2","step3","step4"].map((key, i) => (
              <li key={key} className="share-screen__step">
                <span className="share-screen__step-num">{i + 1}</span>
                <span>{t(`share.${key}`)}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ShareScreen;